import { makeObservable, action, observable, runInAction } from 'mobx';
import StoreUtils from './store-utils';

class ImageStore {
  db;  // Will hold our Dexie database instance
  contentIds = []; // Holds multiple contentIds
  isInitialized = false;

  constructor(uniqueId) {
    console.log('ImageStore constructor');

    this.db = StoreUtils.createDexieUserDB(uniqueId, 'ImageStore');

    this.db.version(2).stores({
      images: 'imageName, data, eTag',
      contentIds: '&id' // & indicates that 'id' is a primary key
    });

    makeObservable(this, {
      contentIds: observable,
      isInitialized: observable,
      initializeAsync: action,
      setContentIds: action,
      clearStore: action,
      saveImageToIndexedDB: action,
      getImageFromIndexedDB: action
    });
  }

  async initializeAsync() {
    const contentIds = await this.db.contentIds.toArray(); // Get all content IDs
    runInAction(() => {
      this.contentIds = contentIds.map(entry => entry.contentId);
      this.isInitialized = true;
    });
  }

  async setContentIds(newContentIds) {
    console.log(`ImageStore setContentIds: ${newContentIds}`);
    await this.db.transaction('rw', this.db.contentIds, async () => {
      await this.db.contentIds.clear(); // Clear existing content IDs first
      for (const id of newContentIds) {
        await this.db.contentIds.put({ id, contentId: id });
      }
    });
    runInAction(() => {
      this.contentIds = newContentIds;
    });
  }

  async clearStore() {
    console.log('Clearing IndexedDB ImageStore...');
    await this.db.transaction('rw', this.db.images, this.db.contentIds, async () => {
      await this.db.images.clear(); // Clear the images
      await this.db.contentIds.clear(); // Clear the contentIds
      runInAction(() => {
        this.contentIds = []; // Reset the contentIds array
      });
    });
  }

  async deleteDatabase() {
    console.log('Deleting IndexedDB database...');
    await this.db.delete();
  }

  // Save image and eTag to IndexedDB
  async saveImageToIndexedDB(imageName, imageData, eTag) {
    console.log(`ImageStore saveImageToIndexedDB: ${imageName}`);
    // Store the image data along with the eTag
    return this.db.images.put({ imageName, data: imageData, eTag });
  }

  // Retrieve image and eTag from IndexedDB
  async getImageFromIndexedDB(imageName) {
    console.log(`ImageStore getImageFromIndexedDB: ${imageName}`);
    // Retrieve both the image data and the eTag
    return this.db.images.get(imageName).then(imageObj => {
      if (imageObj) {
        // Return both data and eTag
        return { data: imageObj.data, eTag: imageObj.eTag };
      }
      return null;
    });
  }
}

const createImageStore = async (uniqueId) => {
  const store = new ImageStore(uniqueId);
  await store.initializeAsync(); // Ensure we initialize the store before using it
  return store;
};

export default createImageStore;
